import { PortableTextBlock, stegaClean, toPlainText } from "next-sanity";
import slugify from "slugify";
import { DateTime } from "luxon";

// Default language for i18n extraction
const DEFAULT_LANGUAGE = "en";

/**
 * Type for internationalized array items
 */
interface InternationalizedArrayItem<T = string> {
  _key: string;
  value?: T;
}

/**
 * Extracts the value for a specific language from an internationalized array.
 * Falls back to the default language (en) if the requested language is not found.
 * Falls back to the first available value if neither is found.
 *
 * @param i18nArray - The internationalized array to extract from
 * @param language - The language key to extract (defaults to "en")
 * @returns The extracted value or undefined if not found
 */
export function getI18nValue<T = string>(
  i18nArray: InternationalizedArrayItem<T>[] | T | null | undefined,
  language: string = DEFAULT_LANGUAGE,
): NonNullable<T> | undefined {
  // If it's already a plain value (not an array), return it
  if (i18nArray === null || i18nArray === undefined) {
    return undefined;
  }

  // If it's not an array, it might already be the value type (for backward compatibility)
  if (!Array.isArray(i18nArray)) {
    return i18nArray as NonNullable<T>;
  }

  // If it's an empty array, return undefined
  if (i18nArray.length === 0) {
    return undefined;
  }

  // Try to find the requested language
  const requestedLang = i18nArray.find((item) => item._key === language);
  if (requestedLang?.value != null) {
    return requestedLang.value;
  }

  // Fall back to default language if different from requested
  if (language !== DEFAULT_LANGUAGE) {
    const defaultLang = i18nArray.find(
      (item) => item._key === DEFAULT_LANGUAGE,
    );
    if (defaultLang?.value != null) {
      return defaultLang.value;
    }
  }

  // Fall back to first available value
  const firstWithValue = i18nArray.find((item) => item.value != null);
  return firstWithValue?.value ?? undefined;
}

/**
 * Extracts a string value from an internationalized array, with an optional fallback.
 *
 * @param i18nArray - The internationalized array to extract from
 * @param fallback - Optional fallback value if extraction fails
 * @param language - The language key to extract (defaults to "en")
 * @returns The extracted string or the fallback
 */
export function getI18nString(
  i18nArray: InternationalizedArrayItem<string>[] | string | null | undefined,
  fallback: string = "",
  language: string = DEFAULT_LANGUAGE,
): string {
  return getI18nValue(i18nArray, language) ?? fallback;
}

export function convertToSlug(
  text?: string,
  { fallback }: { fallback?: string } = { fallback: "top-level" },
) {
  if (!text) return fallback;
  return slugify(text.trim(), {
    lower: true,
    remove: /[^a-zA-Z0-9 ]/g,
  });
}

export function parseChildrenToSlug(children: PortableTextBlock["children"]) {
  if (!children) return "";
  return convertToSlug(children.map((child) => child.text).join(""));
}

interface Hotspot {
  x?: number;
  y?: number;
}

export const getVisualResizeMode = (
  visual: { mediaSource?: unknown } | null | undefined,
): "cover" | "contain" | undefined => {
  const mediaSource = visual?.mediaSource as
    | { _type?: string; customImage?: { resizeMode?: string | null } | null }
    | null
    | undefined;
  if (mediaSource?._type !== "visualImageItem") return undefined;
  const resizeMode = stegaClean(mediaSource.customImage?.resizeMode);
  return resizeMode === "contain" || resizeMode === "cover"
    ? resizeMode
    : undefined;
};

export const getObjectPositionFromHotspot = (hotspot: Hotspot): string => {
  const { x = 0.5, y = 0.5 } = hotspot;
  const topPercentage = Math.round(y * 100);
  const leftPercentage = Math.round(x * 100);

  return `top ${topPercentage}% left ${leftPercentage}%`;
};

type Response<T> = [T, undefined] | [undefined, string];

export async function handleErrors<T>(
  promise: Promise<T>,
): Promise<Response<T>> {
  try {
    const data = await promise;
    return [data, undefined];
  } catch (err) {
    return [
      undefined,
      err instanceof Error ? err.message : JSON.stringify(err),
    ];
  }
}

/**
 * Converts null to undefined for cleaner prop passing.
 * Useful when Sanity returns `null` but React components expect `undefined`.
 */
export function nullToUndefined<T>(value: T | null | undefined): T | undefined {
  return value ?? undefined;
}

const MONTH_ABBR = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
] as const;

/**
 * Formats event start/end dates (ISO "YYYY-MM-DD") into a badge label.
 *
 * Single-day:   "15 MAR"
 * Same month:   "15 - 20 MAR"
 * Cross-month:  "22 JUL - 28 AUG"
 *
 * Uses string parsing to avoid timezone issues with `new Date()`.
 */
export function formatEventDateLabel(
  startDate: string,
  endDate: string,
): string {
  const startParts = startDate.split("-");
  const endParts = endDate.split("-");

  const sm = Number(startParts[1]);
  const sd = Number(startParts[2]);
  const em = Number(endParts[1]);
  const ed = Number(endParts[2]);

  const startMonth = MONTH_ABBR[sm - 1] ?? "JAN";
  const endMonth = MONTH_ABBR[em - 1] ?? "JAN";

  if (startDate === endDate) {
    return `${sd} ${startMonth}`;
  }

  if (sm === em) {
    return `${sd} - ${ed} ${startMonth}`;
  }

  return `${sd} ${startMonth} - ${ed} ${endMonth}`;
}

export function formatShortDate(dateStr: string | null): string | undefined {
  if (!dateStr) return undefined;
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function parseHotelRating(rating: string | null): number {
  if (!rating) return 0;
  const match = rating.match(/^(\d)/);
  return match ? Number(match[1]) : 0;
}

/** True when the href points off-site (absolute http/https URL). */
export function isExternalUrl(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

/**
 * Plain-text lines of a Portable Text block array — one line per block, empty
 * lines dropped. For rendering short authored copy (e.g. an event's Location)
 * where rich text can't go: the header venue column, JSON-LD, calendar links.
 */
export function portableTextLines(blocks: unknown): string[] {
  if (!Array.isArray(blocks)) return [];
  return blocks
    .map((block) => toPlainText([block as PortableTextBlock]).trim())
    .filter(Boolean);
}

export const formatDate = (date: string): string => {
  if (!date || typeof date !== "string") {
    return "";
  }

  const parsedDate = DateTime.fromISO(date);

  if (!parsedDate.isValid) {
    return "";
  }

  return parsedDate.toFormat("dd.MM.yyyy");
};
